import React, { useState, useEffect } from 'react';
import Icon from '../../../components/appIcon';
import axiosInstance from 'utils/axiosInstance';
import { useAuth } from 'components/ui/navigationGuard';

const AttendanceCalendar = ({ attendanceData }) => {
  const { user, isAuthenticated } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [hoveredDate, setHoveredDate] = useState(null);
  const [calendarData, setCalendarData] = useState(attendanceData || []);

  useEffect(() => {
    setCalendarData(attendanceData || []);
  }, [attendanceData]);

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0)?.getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1)?.getDay();
  };

  const getAttendanceForDate = (date) => {
    const dateStr = date?.toISOString()?.split('T')?.[0];
    return calendarData?.find(record => record?.date === dateStr);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present':
        return 'bg-success text-white';
      case 'late':
        return 'bg-warning text-white';
      case 'absent':
        return 'bg-error text-white';
      case 'early_departure':
        return 'bg-orange-500 text-white';
      default:
        return 'bg-muted text-muted-foreground hover:bg-muted/80';
    }
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate?.setMonth(prev?.getMonth() + direction);
      return newDate;
    });
  };

  // Function to fetch calendar data for the selected month
  const fetchCalendarData = async (year, month) => {
    try {
      const response = await axiosInstance.get(`/employee/me/attendance-calendar/${user.employee_id}`, {
        params: { year, month }
      });
      setCalendarData(response?.data?.data?.attendanceData || []);
    } catch (error) {
      console.error("Error fetching calendar data:", error);
    }
  };

  useEffect(() => {
    // Fetch data when currentDate changes
    fetchCalendarData(currentDate.getFullYear(), currentDate.getMonth() + 1);
  }, [currentDate]);

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const today = new Date();

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days?.push(
        <div key={`empty-${i}`} className="h-10 w-10"></div>
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const attendance = getAttendanceForDate(date);
      const isToday = date?.toDateString() === today?.toDateString();
      const isFuture = date > today;

      days?.push(
        <div
          key={day}
          className="relative"
          onMouseEnter={() => setHoveredDate(attendance)}
          onMouseLeave={() => setHoveredDate(null)}
        >
          <div
            className={`h-10 w-10 rounded-lg flex items-center justify-center text-sm font-medium cursor-pointer enterprise-transition ${
              isToday ? 'ring-2 ring-primary ring-offset-2' : ''
            } ${
              isFuture ? 'opacity-50 cursor-not-allowed' : ''
            } ${
              attendance ? getStatusColor(attendance?.status) : 'hover:bg-muted'
            }`}
          >
            {day}
          </div>
          {attendance && (
            <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2">
              <div className={`w-2 h-2 rounded-full ${getStatusColor(attendance?.status)}`}></div>
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 enterprise-shadow-card">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-foreground">Attendance Calendar</h2>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => navigateMonth(-1)}
            className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-muted enterprise-transition"
          >
            <Icon name="ChevronLeft" size={16} />
          </button>
          <span className="text-sm font-medium text-foreground min-w-[120px] text-center">
            {currentDate?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </span>
          <button
            onClick={() => navigateMonth(1)}
            className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-muted enterprise-transition"
          >
            <Icon name="ChevronRight" size={16} />
          </button>
        </div>
      </div>
      {/* Calendar Header */}
      <div className="grid grid-cols-7 gap-2 mb-4">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map(day => (
          <div key={day} className="h-8 flex items-center justify-center text-xs font-medium text-muted-foreground">
            {day}
          </div>
        ))}
      </div>
      {/* Calendar Days */}
      <div className="grid grid-cols-7 gap-2 mb-6">
        {renderCalendarDays()}
      </div>
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-success rounded-full"></div>
          <span className="text-muted-foreground">Present</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-warning rounded-full"></div>
          <span className="text-muted-foreground">Late</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-error rounded-full"></div>
          <span className="text-muted-foreground">Absent</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
          <span className="text-muted-foreground">Early Leave</span>
        </div>
      </div>
      {/* Hover Tooltip */}
      {hoveredDate && (
        <div className="absolute z-10 bg-popover border border-border rounded-lg p-3 enterprise-shadow-modal">
          <div className="text-sm">
            <p className="font-medium text-foreground">{new Date(hoveredDate?.date)?.toLocaleDateString()}</p>
            <p className="text-muted-foreground capitalize">Status: {hoveredDate?.status}</p>
            {hoveredDate?.checkIn && (
              <p className="text-muted-foreground">Check In: {new Date(hoveredDate?.checkIn)?.toLocaleTimeString()}</p>
            )}
            {hoveredDate?.checkOut && (
              <p className="text-muted-foreground">Check Out: {new Date(hoveredDate?.checkOut)?.toLocaleTimeString()}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceCalendar;